/* eslint-disable @typescript-eslint/no-inferrable-types */
import React, { useEffect, useState, useMemo } from 'react';
import { PaperClipOutlined, UploadOutlined } from '@ant-design/icons';
import { Form, Row, Col, Card, Upload, Button, message, Table } from 'antd';
import { uploadDocument } from '@app/api/import.api';

interface DocumentChecklistProps {
  initialformFields: DocumentType[][] | DocumentType[];
  formFields: {
    name: string;
    document_id: string;
  }[];
  status: string | null;
  import_id: string | null;
  importData: any;
  initialImportLinesDocFormFields?: DocumentType[];
  submittedDocs?: DocumentType[];
  productNames: any;
}

interface DocumentType {
  image_url?: string;
  name: string;
  document_id: string;
  status: string;
  url?: string;
  catalog_product_id?: string;
  file?: string;
}

const DocumentChecklist: React.FC<DocumentChecklistProps> = ({
  initialformFields: propInitialFields,
  formFields,
  status,
  import_id,
  importData,
  initialImportLinesDocFormFields,
  productNames,
  submittedDocs,
}) => {
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [documentFields, setDocumentFields] = useState<Array<DocumentType>>([]);
  const [fileList, setFileList] = useState<Record<string, any>>({});

  const importDocumentCollections = propInitialFields || [];

  const userType = React.useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user?.usertype || '';
    } catch {
      return '';
    }
  }, []);

  const canUpload = status === 'PENDING_DOCUMENT' && userType === 'CUSTOMER';

  // Sync documentFields from propInitialFields
  useEffect(() => {
    if (Array.isArray(propInitialFields)) {
      const fields = Array.isArray(propInitialFields[0])
        ? (propInitialFields as DocumentType[][]).flat()
        : (propInitialFields as DocumentType[]);
      setDocumentFields(fields);
    }
  }, [propInitialFields]);

  // Build initial fileList from documentFields AND submittedDocs
  useEffect(() => {
    const initialFiles: Record<string, any> = {};

    // Helper: resolve the file URL from either field name the API may return
    const resolveUrl = (doc: any): string | undefined => doc.document_url || doc.image_url || doc.url || undefined;

    // First load from documentFields (general docs)
    documentFields.forEach((field) => {
      const fileUrl = resolveUrl(field);
      if (fileUrl) {
        const productId = field.catalog_product_id || import_id;
        const fileKey = `${field.document_id}_${productId}`;
        initialFiles[fileKey] = [
          {
            uid: fileKey,
            name: field.file || field.name,
            status: 'done',
            url: fileUrl,
          },
        ];
      }
    });

    // Also load from submittedDocs so they appear inline in their upload fields
    submittedDocs?.forEach((doc) => {
      const fileUrl = resolveUrl(doc);
      if (fileUrl) {
        const productId = doc.catalog_product_id || import_id;
        const fileKey = `${doc.document_id}_${productId}`;
        initialFiles[fileKey] = [
          {
            uid: fileKey,
            name: doc.file || doc.name,
            status: 'done',
            url: fileUrl,
          },
        ];
      }
    });

    // Load from initialImportLinesDocFormFields as well
    initialImportLinesDocFormFields?.forEach((doc) => {
      const fileUrl = resolveUrl(doc);
      if (fileUrl) {
        const productId = doc.catalog_product_id || import_id;
        const fileKey = `${doc.document_id}_${productId}`;
        initialFiles[fileKey] = [
          {
            uid: fileKey,
            name: doc.file || doc.name,
            status: 'done',
            url: fileUrl,
          },
        ];
      }
    });

    setFileList(initialFiles);
  }, [documentFields, import_id, submittedDocs, initialImportLinesDocFormFields]);

  // Build a set of document_ids that are already submitted (to suppress from table)
  const submittedDocIdSet = React.useMemo(() => {
    const set = new Set<string>();
    submittedDocs?.forEach((doc) => {
      set.add(doc.document_id);
    });
    return set;
  }, [submittedDocs]);

  const processUploadResponse = (response: any) => {
    const importDocuments = response?.data?.import_documents;
    if (!importDocuments) return;

    const responseDocuments: any[] = [];

    Object.values(importDocuments).forEach((modelGroup: any) => {
      Object.values(modelGroup).forEach((docArray: any) => {
        if (Array.isArray(docArray)) {
          docArray.forEach((doc: any) => responseDocuments.push(doc));
        }
      });
    });

    if (responseDocuments.length > 0) {
      setFileList((prev) => {
        const newFileList = { ...prev };
        responseDocuments.forEach((doc) => {
          const fileUrl = doc.image_url || doc.url;
          if (fileUrl) {
            const productId = doc.catalog_product_id || import_id;
            const fileKey = `${doc.document_id}_${productId}`;
            newFileList[fileKey] = [
              {
                uid: fileKey,
                name: doc.file || doc.name,
                status: 'done',
                url: fileUrl,
              },
            ];
          }
        });
        return newFileList;
      });
    }
  };

  const handleUpload = async (documentId: string, file: File, productId: string) => {
    if (!import_id) {
      message.error('Import ID is required');
      return;
    }

    const uploadKey = `${documentId}_${productId}`;
    try {
      setUploading((prev) => ({ ...prev, [uploadKey]: true }));

      const formData = new FormData();
      formData.append('document_id', documentId);
      formData.append('file', file, file.name);
      formData.append('import_id', import_id);

      const response = await uploadDocument(Number(import_id), formData);

      if (response?.data?.import_documents) {
        processUploadResponse(response);
        message.success(`${file.name} uploaded successfully`);
      } else {
        message.success(`${file.name} uploaded successfully`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      message.error(`Failed to upload ${file.name}`);
    } finally {
      setUploading((prev) => ({ ...prev, [uploadKey]: false }));
    }
  };

  const handleChange = async (documentId: string, file: any, productId: string) => {
    // Only take the last selected file — replaces any previous selection
    const newFiles = file.fileList.filter((f: any) => f.originFileObj);
    const fileKey = `${documentId}_${productId}`;

    if (newFiles.length > 0) {
      // Keep only the most recently added file to prevent duplicates on re-upload
      const latestFile = newFiles[newFiles.length - 1];

      setFileList((prev) => ({
        ...prev,
        [fileKey]: [latestFile],
      }));

      await handleUpload(documentId, latestFile.originFileObj, productId);
    } else {
      message.error('No file detected, please try again.');
    }
  };

  // Renders a single upload card — shared by all three sections
  const renderUploadCard = (docName: string, documentId: string, productId: string) => {
    const fileKey = `${documentId}_${productId}`;
    const isUploading = uploading[fileKey] || false;
    const currentFiles = (fileList[fileKey] || []).slice(0, 1);
    const hasFile = currentFiles.length > 0;
    const fileEntry = hasFile ? currentFiles[0] : null;
    const fileUrl = fileEntry?.url;
    const fileName = fileEntry?.name;

    return (
      <Col xs={24} sm={12} key={fileKey}>
        <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200" style={{ minHeight: 160 }}>
          <div className="flex flex-col gap-3">
            {/* Label rendered manually so it always sits above the upload area */}
            <span className="text-gray-700 font-medium text-sm">{docName}</span>
            <div
              className="bg-gray-50 p-4 rounded-lg border border-gray-200 flex flex-col gap-3"
              style={{ minHeight: 90 }}
            >
              {/* Hide the Ant Design built-in file list to prevent overflow outside card */}
              <Upload
                accept=".pdf,.jpg,.jpeg,.png"
                beforeUpload={() => false}
                fileList={currentFiles}
                onChange={(file) => handleChange(documentId, file, productId)}
                showUploadList={false}
                className="w-full"
                disabled={!canUpload}
              >
                <Button size="small" disabled={isUploading || !canUpload}>
                  <UploadOutlined className="text-gray-500" />
                  <span>{isUploading ? 'Uploading...' : hasFile ? 'Re-Upload' : 'Upload'}</span>
                </Button>
              </Upload>

              {/* Manually render file info inside the bounded card area */}
              {hasFile && (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-1 min-w-0">
                    <PaperClipOutlined className="text-gray-400 flex-shrink-0 text-xs" />
                    {fileUrl ? (
                      <a
                        href={fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 text-xs truncate hover:underline"
                        title={fileName}
                      >
                        {fileName}
                      </a>
                    ) : (
                      <span className="text-gray-600 text-xs truncate" title={fileName}>
                        {fileName}
                      </span>
                    )}
                  </div>
                  {/* Status row */}
                  <div className="text-green-500 text-sm">✓ File uploaded</div>
                </div>
              )}
            </div>
          </div>
        </Card>
      </Col>
    );
  };

  const renderDocumentFields = () => {
    const docs = Array.isArray(importDocumentCollections)
      ? Array.isArray(importDocumentCollections[0])
        ? (importDocumentCollections as DocumentType[][]).flat()
        : (importDocumentCollections as DocumentType[])
      : [];

    return (
      <>
        <h3 className="text-lg font-semibold mb-6">General Document Checklist</h3>
        {docs.length > 0 ? (
          <Row gutter={[16, 16]}>
            {docs.map((docCollection: DocumentType) => {
              const documentId = docCollection.document_id;
              const productId = import_id || '';
              return renderUploadCard(docCollection.name, documentId, productId);
            })}
          </Row>
        ) : (
          <Col span={24}>
            <div className="p-4 bg-gray-50 rounded-lg text-gray-600 text-center">
              No documents available for this category.
            </div>
          </Col>
        )}
      </>
    );
  };

  const renderProductBasedDocuments = () => {
    let lastRenderedProductName: string | null = null;

    const chunks: DocumentType[][] = [];
    if (initialImportLinesDocFormFields) {
      for (let i = 0; i < initialImportLinesDocFormFields.length; i += 2) {
        chunks.push(initialImportLinesDocFormFields.slice(i, i + 2));
      }
    }

    return (
      <>
        <br />
        <h3 className="text-lg font-semibold mb-4 mt-10">Product Based File Upload</h3>
        {initialImportLinesDocFormFields && initialImportLinesDocFormFields.length > 0 ? (
          chunks.map((chunk, chunkIndex) => (
            <div key={`chunk-${chunkIndex}`} className="mb-8">
              {chunk.map((docCollection: DocumentType) => {
                const productName = productNames;
                const shouldRenderProductName = productName !== lastRenderedProductName;
                if (shouldRenderProductName) {
                  lastRenderedProductName = productName;
                }
                return (
                  <React.Fragment key={`name-${docCollection.document_id}`}>
                    {shouldRenderProductName && <h3 className="text-base font-medium mb-2">{productName}</h3>}
                  </React.Fragment>
                );
              })}
              <Row gutter={[16, 16]}>
                {chunk.map((docCollection: DocumentType) => {
                  const documentId = docCollection.document_id;
                  const productId = docCollection.catalog_product_id || import_id || '';
                  return renderUploadCard(docCollection.name, documentId, productId);
                })}
              </Row>
            </div>
          ))
        ) : (
          <Col span={24}>
            <div className="p-4 bg-gray-50 rounded-lg text-gray-600 text-center">
              No documents available for this product.
            </div>
          </Col>
        )}
      </>
    );
  };

  return (
    <div className="p-6 bg-gray-50 rounded-lg">
      <Form layout="vertical">
        {renderDocumentFields()}
        {renderProductBasedDocuments()}
      </Form>
    </div>
  );
};

export default DocumentChecklist;
